/**
 * PIP-407 — which evergreen "daily-repost" series are PENALIZED, as opposed to
 * merely collapsed.
 *
 * Every detected series (same source, same normalized title, same venue, on
 * many distinct days) is collapsed to its next occurrence on public list
 * surfaces, whatever its source: a Ticketmaster theater run reads better as
 * one card too. But only the sources below get the `SERIES_EVERGREEN`
 * LOW_CONFIDENCE cap and are kept off the hero (`/recommendations/for-you`)
 * and the category top-picks shelf.
 *
 * Why these two: they are the open, self-serve lanes. Anyone can publish an
 * Eventbrite listing, and venue sites post whatever the venue's CMS holds, so
 * a standing commerce product (a class, a membership, a reservation, a tour)
 * reposted as a new dated event every day arrives through them. A
 * Ticketmaster series is a box-office run of real performances (a touring
 * musical, an ice show): collapsing it is a presentation choice, but demoting
 * it or pulling it off the rails would hide a headline show.
 *
 * Values are `Event.source` as the ingest lanes write it
 * (`EVENTBRITE_APIFY_SOURCE`, `VENUE_SITE_SOURCE` in data-pipeline). Adding a
 * source here widens BOTH the cap and the rail exclusion; it needs a test
 * proving that lane's real series are standing products, not performances.
 */
export const SERIES_EVERGREEN_SOURCES = ['eventbrite', 'venue-site'] as const;

/** One of {@link SERIES_EVERGREEN_SOURCES}. */
export type SeriesEvergreenSource = (typeof SERIES_EVERGREEN_SOURCES)[number];

/**
 * True when a series from `source` is capped and kept off the rails.
 * Case- and whitespace-insensitive; `null` / unknown sources are never
 * penalized (their series still collapse).
 */
export function isSeriesEvergreenSource(source: string | null | undefined): boolean {
  if (typeof source !== 'string') return false;
  const normalized = source.trim().toLowerCase();
  return (SERIES_EVERGREEN_SOURCES as readonly string[]).includes(normalized);
}
